import React, { useCallback, useMemo, useRef, useState } from "react";
import { Select, Input, Button, Divider, Space, message } from "antd";
import type { InputRef } from "antd";

import { useGetPropertyLocationHierarchyQuery } from "graphql/queries/get-property-location-hierarchy.generated";
import { useCreateBuildingMutation } from "graphql/mutations/create-building.generated";
import { useCreateFloorMutation } from "graphql/mutations/create-floor.generated";
import { useCreateRoomMutation } from "graphql/mutations/create-room.generated";

// Styles are global and loaded centrally via src/styles/App.scss (house convention:
// components do not import their own .scss).

export interface LocationHierarchyValue {
	buildingId: string | null;
	floorId: string | null;
	roomId: string | null;
}

interface LocationHierarchySelectProps {
	// The property whose Building → Floor → Room tree these dropdowns scope to.
	propertyId?: string | null;
	value: LocationHierarchyValue;
	onChange: (value: LocationHierarchyValue) => void;
	disabled?: boolean;
}

const emptyValue: LocationHierarchyValue = { buildingId: null, floorId: null, roomId: null };

/**
 * Cascading Building → Floor → Room selectors scoped to a property, with an inline
 * "add new" affordance on each level. Mirrors the cascade used in Ollie evidence
 * collection, but here the user can also create hierarchy rows on the fly.
 *
 * Only the deepest selected level needs to be persisted by the caller: the backend
 * derives the ancestors. We still expose the full {buildingId, floorId, roomId} so a
 * caller can send whichever it wants and pre-fill the dropdowns on edit.
 */
const LocationHierarchySelect: React.FC<LocationHierarchySelectProps> = ({ propertyId, value, onChange, disabled }) => {
	const { data, refetch } = useGetPropertyLocationHierarchyQuery({
		variables: { propertyId },
		skip: !propertyId,
		fetchPolicy: "cache-and-network"
	});

	const [createBuilding, { loading: creatingBuilding }] = useCreateBuildingMutation();
	const [createFloor, { loading: creatingFloor }] = useCreateFloorMutation();
	const [createRoom, { loading: creatingRoom }] = useCreateRoomMutation();

	const [newBuildingName, setNewBuildingName] = useState("");
	const [newFloorName, setNewFloorName] = useState("");
	const [newRoomName, setNewRoomName] = useState("");
	const buildingInputRef = useRef<InputRef>(null);
	const floorInputRef = useRef<InputRef>(null);
	const roomInputRef = useRef<InputRef>(null);

	const { buildings, floorsForBuilding, roomsForFloor, buildingOptions, floorOptions, roomOptions } = useMemo(() => {
		const buildings = data?.buildings ?? [];
		const selectedBuilding = buildings.find(b => b.id === value.buildingId) ?? null;
		const floorsForBuilding = selectedBuilding?.floors ?? [];
		const selectedFloor = floorsForBuilding.find(f => f.id === value.floorId) ?? null;
		const roomsForFloor = selectedFloor?.rooms ?? [];

		return {
			buildings,
			floorsForBuilding,
			roomsForFloor,
			buildingOptions: buildings.map(b => ({ value: b.id, label: b.name })),
			floorOptions: floorsForBuilding.map(f => ({ value: f.id, label: f.name })),
			roomOptions: roomsForFloor.filter(r => r.is_inspectable !== false).map(r => ({ value: r.id, label: r.name }))
		};
	}, [data, value.buildingId, value.floorId]);

	const handleAddBuilding = useCallback(async () => {
		const name = newBuildingName.trim();
		if (!propertyId || !name) return;
		try {
			const res = await createBuilding({
				variables: { input: { property_id: propertyId, name, sort_order: buildings.length } }
			});
			const created = res.data?.insert_buildings_one;
			if (created) {
				await refetch();
				onChange({ buildingId: created.id, floorId: null, roomId: null });
				setNewBuildingName("");
			}
		} catch (error) {
			message.error("Could not create the building.");
			console.log("createBuilding error: ", error);
		}
	}, [newBuildingName, propertyId, buildings.length, createBuilding, refetch, onChange]);

	const handleAddFloor = useCallback(async () => {
		const name = newFloorName.trim();
		if (!propertyId || !value.buildingId || !name) return;
		try {
			const res = await createFloor({
				variables: {
					input: {
						building_id: value.buildingId,
						property_id: propertyId,
						name,
						sort_order: floorsForBuilding.length
					}
				}
			});
			const created = res.data?.insert_floors_one;
			if (created) {
				await refetch();
				onChange({ buildingId: value.buildingId, floorId: created.id, roomId: null });
				setNewFloorName("");
			}
		} catch (error) {
			message.error("Could not create the floor.");
			console.log("createFloor error: ", error);
		}
	}, [newFloorName, propertyId, value.buildingId, floorsForBuilding.length, createFloor, refetch, onChange]);

	const handleAddRoom = useCallback(async () => {
		const name = newRoomName.trim();
		if (!propertyId || !value.floorId || !name) return;
		try {
			const res = await createRoom({
				variables: {
					input: {
						floor_id: value.floorId,
						property_id: propertyId,
						name,
						sort_order: roomsForFloor.length
					}
				}
			});
			const created = res.data?.insert_rooms_one;
			if (created) {
				await refetch();
				onChange({ buildingId: value.buildingId, floorId: value.floorId, roomId: created.id });
				setNewRoomName("");
			}
		} catch (error) {
			message.error("Could not create the room.");
			console.log("createRoom error: ", error);
		}
	}, [newRoomName, propertyId, value, roomsForFloor.length, createRoom, refetch, onChange]);

	const isDisabled = disabled || !propertyId;

	return (
		<div className="location-hierarchy-select-container">
			<div className="location-hierarchy-select-row">
				<Select
					className="location-hierarchy-select-field"
					popupMatchSelectWidth={false}
					allowClear
					disabled={isDisabled}
					placeholder="Building"
					value={value.buildingId ?? undefined}
					options={buildingOptions}
					onChange={buildingId => onChange({ buildingId: buildingId ?? null, floorId: null, roomId: null })}
					dropdownRender={menu => (
						<>
							{menu}
							<Divider className="location-hierarchy-select-divider" />
							<Space.Compact className="location-hierarchy-select-add-row">
								<Input
									ref={buildingInputRef}
									placeholder="New building"
									value={newBuildingName}
									onChange={e => setNewBuildingName(e.target.value)}
									onKeyDown={e => e.stopPropagation()}
									onPressEnter={handleAddBuilding}
								/>
								<Button type="primary" loading={creatingBuilding} onClick={handleAddBuilding}>
									Add
								</Button>
							</Space.Compact>
						</>
					)}
				/>
				<Select
					className="location-hierarchy-select-field"
					popupMatchSelectWidth={false}
					allowClear
					disabled={isDisabled || !value.buildingId}
					placeholder="Floor"
					value={value.floorId ?? undefined}
					options={floorOptions}
					onChange={floorId => onChange({ buildingId: value.buildingId, floorId: floorId ?? null, roomId: null })}
					dropdownRender={menu => (
						<>
							{menu}
							<Divider className="location-hierarchy-select-divider" />
							<Space.Compact className="location-hierarchy-select-add-row">
								<Input
									ref={floorInputRef}
									placeholder="New floor"
									value={newFloorName}
									onChange={e => setNewFloorName(e.target.value)}
									onKeyDown={e => e.stopPropagation()}
									onPressEnter={handleAddFloor}
								/>
								<Button type="primary" loading={creatingFloor} onClick={handleAddFloor}>
									Add
								</Button>
							</Space.Compact>
						</>
					)}
				/>
				<Select
					className="location-hierarchy-select-field"
					popupMatchSelectWidth={false}
					allowClear
					disabled={isDisabled || !value.floorId}
					placeholder="Room/Area"
					value={value.roomId ?? undefined}
					options={roomOptions}
					onChange={roomId =>
						onChange({ buildingId: value.buildingId, floorId: value.floorId, roomId: roomId ?? null })
					}
					dropdownRender={menu => (
						<>
							{menu}
							<Divider className="location-hierarchy-select-divider" />
							<Space.Compact className="location-hierarchy-select-add-row">
								<Input
									ref={roomInputRef}
									placeholder="New room/area"
									value={newRoomName}
									onChange={e => setNewRoomName(e.target.value)}
									onKeyDown={e => e.stopPropagation()}
									onPressEnter={handleAddRoom}
								/>
								<Button type="primary" loading={creatingRoom} onClick={handleAddRoom}>
									Add
								</Button>
							</Space.Compact>
						</>
					)}
				/>
			</div>
		</div>
	);
};

export { emptyValue as emptyLocationHierarchyValue };
export default LocationHierarchySelect;
